import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'

import GlassCard from '@/components/GlassCard'
import Button from '@/components/Button'
import LangToggle from '@/components/LangToggle'
import type { ScoreEntry } from '@/lib/leaderboard'

import styles from './start.module.scss'

export interface StartScreenProps {
  onStart: (name: string) => void
  lastName: string
  topToday: ScoreEntry[]
  muted: boolean
  onToggleMute: () => void
}

const NAME_MIN = 2
const NAME_MAX = 24
const BOARD_ROWS = 5

const HOW_ROWS: readonly { key: string; chip: string }[] = [
  { key: 'start.howLane', chip: styles.chipLane },
  { key: 'start.howCollect', chip: styles.chipCollect },
  { key: 'start.howBobi', chip: styles.chipBobi },
  { key: 'start.howSokar', chip: styles.chipSokar },
]

/** `1 234` with a narrow no-break space, so scores never wrap mid-number. */
function groupDigits(value: number): string {
  return String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, '\u202f')
}

function SpeakerIcon({ muted }: { muted: boolean }) {
  return (
    <svg
      width='20'
      height='20'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.8'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
      focusable='false'
    >
      <path d='M11 5 6 9H3v6h3l5 4V5Z' />
      {muted ?
        <>
          <path d='m17 9.5 5 5' />
          <path d='m22 9.5-5 5' />
        </>
      : <>
          <path d='M15.5 8.8a4.4 4.4 0 0 1 0 6.4' />
          <path d='M18.6 6a8.6 8.6 0 0 1 0 12' />
        </>
      }
    </svg>
  )
}

export default function StartScreen({
  onStart,
  lastName,
  topToday,
  muted,
  onToggleMute,
}: StartScreenProps) {
  const { t } = useTranslation()
  const [name, setName] = useState(lastName)
  const [invalid, setInvalid] = useState(false)

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const trimmed = name.trim()
    if (trimmed.length < NAME_MIN || trimmed.length > NAME_MAX) {
      setInvalid(true)
      return
    }
    setInvalid(false)
    onStart(trimmed)
  }

  const board = topToday.slice(0, BOARD_ROWS)

  return (
    <div className={styles.screen}>
      <header className={styles.topBar}>
        <span className={styles.wordmark}>ONITSOFT</span>
        <div className={styles.topActions}>
          <LangToggle />
          <button
            type='button'
            className={muted ? `${styles.mute} ${styles.muteOff}` : styles.mute}
            aria-label={muted ? t('common.soundOff') : t('common.soundOn')}
            aria-pressed={!muted}
            onClick={onToggleMute}
          >
            <SpeakerIcon muted={muted} />
          </button>
        </div>
      </header>

      <main className={styles.body}>
        <section className={`${styles.hero} ${styles.rise}`}>
          <span className={styles.badge}>{t('start.badge')}</span>
          <h1 className={styles.title}>
            {t('start.title')} <span className={styles.accent}>{t('start.titleAccent')}</span>
          </h1>
          <p className={styles.subtitle}>{t('start.subtitle')}</p>
        </section>

        <form
          className={`${styles.form} ${styles.rise} ${styles.d1}`}
          onSubmit={submit}
          noValidate
        >
          <label
            className={styles.label}
            htmlFor='player-name'
          >
            {t('start.nameLabel')}
          </label>
          <input
            id='player-name'
            className={invalid ? `${styles.input} ${styles.inputError}` : styles.input}
            type='text'
            value={name}
            maxLength={NAME_MAX}
            autoComplete='given-name'
            enterKeyHint='go'
            placeholder={t('start.namePlaceholder')}
            aria-invalid={invalid}
            onChange={(event) => {
              setName(event.target.value)
              if (invalid) setInvalid(false)
            }}
          />
          {invalid && <p className={styles.error}>{t('start.nameError')}</p>}
          <Button
            type='submit'
            block
          >
            {t('start.startBtn')}
          </Button>
        </form>

        <GlassCard className={`${styles.how} ${styles.rise} ${styles.d2}`}>
          <h2 className={styles.sectionTitle}>{t('start.howTitle')}</h2>
          {HOW_ROWS.map((row) => (
            <p
              key={row.key}
              className={styles.howRow}
            >
              <span className={`${styles.chip} ${row.chip}`} />
              {t(row.key)}
            </p>
          ))}
        </GlassCard>

        <GlassCard className={`${styles.boardCard} ${styles.rise} ${styles.d3}`}>
          <h2 className={styles.sectionTitle}>{t('start.boardTitle')}</h2>
          {board.length === 0 ?
            <p className={styles.boardEmpty}>{t('start.boardEmpty')}</p>
          : <ol className={styles.board}>
              {board.map((entry, index) => (
                <li
                  key={`${entry.code}-${entry.ts}`}
                  className={styles.boardRow}
                >
                  <span className={styles.boardRank}>#{index + 1}</span>
                  <span className={styles.boardName}>{entry.name}</span>
                  <span className={styles.boardScore}>
                    {groupDigits(entry.score)}
                    <em className={styles.unit}>{t('common.pts')}</em>
                  </span>
                </li>
              ))}
            </ol>
          }
        </GlassCard>
      </main>

      <footer className={styles.footer}>{t('start.privacy')}</footer>
    </div>
  )
}
